import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Routes } from '../constants';
import DateTime from '../dateTime';
import { generateRoute } from './routeUtils';

/**
 * Converts the provided practice schedule time to 24 hour format.
 * @param {String} scheduleTime The time in 12 hour format (e.g. 10:00:00 AM).
 * @returns Array
 */
export function convertPracticeScheduleTimeTo24Hour(scheduleTime) {
  const formattedTime = [0, 0];

  if (scheduleTime) {
    const [time, meridiem] = scheduleTime.split(' ');
    const [hour, minute] = time.split(':');
    const anteMeridiem = 'AM';
    const postMeridiem = 'PM';
    const hourNumber = parseInt(hour);

    let setMeridiem = meridiem;

    if ( !meridiem ) {
      setMeridiem = hourNumber < 12 ? 'AM' : 'PM';
    }

    if (setMeridiem === anteMeridiem) {
      formattedTime[0] = hourNumber;
    }
    else if (setMeridiem === postMeridiem) {
      formattedTime[0] = hourNumber !== 12 ? hourNumber + 12 : hourNumber;
    }

    formattedTime[1] = parseInt(minute);
  }

  return formattedTime;
}

/**
 * Returns the booking url for a practice.
 * @param {Object} config The tenant configuration.
 * @param {Object} practice The practice.
 * @returns String
 */
export function getBookingUrl(config, practice) {
  let bookingUrl = '';

  if (practice) {
    const { appointmentUrl } = practice;

    if (!String.isNullOrEmpty(appointmentUrl)) {
      bookingUrl = appointmentUrl;
    }
    else {
      const { identifier } = practice;
      const { eZEmbedMainURL } = window.locatorTenancy;
      const detailsRoute = generateRoute(config, Routes.details);
      const rootUrl = eZEmbedMainURL || '';

      bookingUrl = `${rootUrl}/${detailsRoute}/${identifier}`;
    }
  }

  return bookingUrl;
}

/**
 * Returns the practice schedule closed hours.
 * @param {Object} config The tenant configuration.
 * @param {String} day The day of the week.
 * @param {Object} practice The practice.
 * @returns String
 */
export function getPracticeCloseHours(config, day, practice) {
  const { schedule } = practice;
  let closeHours = '';
  let meridiem = '';
  const dayLowerCased = day?.toLowerCase();

  if (!schedule || !schedule[dayLowerCased]) {
    return closeHours;
  }

  const closingTime = schedule[dayLowerCased].closeAlt ?? schedule[dayLowerCased].close;

  if (!practiceHasSchedule(schedule) || !closingTime) {
    return closeHours;
  }

  const [hour, minute] = convertPracticeScheduleTimeTo24Hour(closingTime);
  const closeDateTime = new DateTime();

  meridiem = hour < 12 ? 'AM' : 'PM';

  closeDateTime
    .setHour(hour)
    .setMinute(minute);
    
  if (config.show_store_hours_in_24_format) {
    closeHours = closeDateTime.format('H:mm');
  }
  else {
    closeHours = closeDateTime.format('h:mm');
  }

  return `${closeHours} ${meridiem}`;
}

/**
 * Returns the operating company for booking based on the country.
 * @param {String} country The country.
 * @returns {String} The operating company based on the provided country.
 */
export function getTenantOperatingCompany(country) {
  switch (country) {
    case 'United Kingdom':
      return 'ESSILOR Ltd';
    case 'Italy':
      return 'ESSILOR ITALIA';
    case 'France':
      return 'ESSILOR FRANCE';
    case 'Poland':
      return 'ESSILOR POLAND';
    case 'Austria':
      return 'ESSILOR AUSTRIA';
    case 'Czech Republic':
      return 'ESSILOR CZECH REPUBLIC';
    case 'Germany':
    case 'Deutschland':
      return 'ESSILOR GmbH';
    case 'Denmark':
    case 'DANMARK':
      return 'ESSILOR DANMARK A/S';
    case 'Spain':
    case 'España':
      return 'ESSILOR SPAIN';
    case 'Finland':
      return 'ESSILOR OY';
    case 'Croatia':
      return 'ESSILOR HRVATSKA';
    case 'Hungary':
      return 'ESSILOR OPTIKA KTF';
    case 'Netherlands':
    case 'Nederland':
      return 'ESSILOR NL';
    case 'Norway':
      return 'ESSILOR NORGE AS';
    case 'Portugal':
      return 'ESSILOR PORTUGAL';
    case 'Romania':
    case 'România':
      return 'ESSILOR ROMANIA';
    case 'Serbia':
      return 'ESSILOR SERBIJA';
    case 'Sweden':
      return 'ESSILOR AB';
    case 'Slovenia':
      return 'ESSILOR SLOVENIA';
    case 'Slovakia':
      return 'ESSILOR SLOVAKIA';
    case 'Bulgaria':
    case 'България':
      return 'ESSILOR BULGARIA';
    case 'BE':
    case 'Belgium':
    case 'LU':
      return 'ESSILOR BELGIUM';
    case 'CH':
    case 'Switzerland':
      return 'ESSILOR SWITZERLAND';
    default:
      return 'ESSILOR Ltd';
  }
}

/**
 * Returns practice formatted phone number.
 * @param {Object} config The tenant configuration.
 * @param {String} phone The practice phone number.
 * @returns String
 */
export function getPracticeFormattedPhoneNumber(config, phone) {
  let formattedPhone = phone;

  if (config.use_phone_formatting && phone) {
    let countryCode = 'us';

    if (config.country_Code) {
      countryCode = config.country_code;
    }
    else if (config.country_geo_constraint && config.country_geo_constraint.length > 0) {
      countryCode = config.country_geo_constraint[0].toUpperCase();
    }

    formattedPhone = parsePhoneNumberFromString(phone, countryCode);

    if (formattedPhone) {
      formattedPhone = formattedPhone.formatNational();
    }
    else {
      formattedPhone = phone;
    }
  }

  return formattedPhone;
}

/**
 * Returns tenant unit of measurement (e.g. MI or KM).
 * @param {Object} config The tenant configuration.
 * @returns String
 */
export function getTenantUnitOfMeasure(config) {
  let unitOfMeasure = 'MI';

  if (!config?.show_distance_in_imperial_units) {
    unitOfMeasure = 'KM';
  }

  return unitOfMeasure;
}

/**
 * Returns a value representing the current tenant's primary color.
 */
export function getTenantPrimaryColor() {
  return getComputedStyle(document.documentElement).getPropertyValue('--colors-primary');
}

/**
 * Returns a value representing the current tenant's secondary color.
 */
export function getTenantSecondaryColor() {
  return getComputedStyle(document.documentElement).getPropertyValue('--colors-secondary');
}

/**
 * Returns a value that determines whether a practice has a schedule.
 * @param {Object} schedule The practice schedule.
 * @returns Boolean
 */
export function practiceHasSchedule(schedule) {
  let hasSchedule = false;

  if (!Object.isEmpty(schedule)) {
    const weekdays = DateTime.weekdays();

    for (let index = 0; index < weekdays.length; ++index) {
      const weekday = weekdays[index].toLocaleLowerCase();
      const scheduleDay = schedule ?  schedule[weekday] : null;

      if (scheduleDay && scheduleDay.open && scheduleDay.close) {
        hasSchedule = true;
        break;
      }
    }
  }

  return hasSchedule;
}

export const getClassNameOrientation = (base, condition) => {
  return condition ? `${base}--flipped` : base;
};


/**
 * Replace a configured parameter with a configured mapping in a url.
 * @param {*} replacementConfig Configuration object that contains mapping and parameter to replace.
 * @param {*} inputParameter Parameter to map to desired replacement in the config object.
 * @param {*} url Url that potentially contains the replaceable string.
 */
export function replaceParameterInBookingUrl(replacementConfig, url) {
  let updatedUrl = formatUrlWithoutProtocol(url);
  const currentLang = window.locatorTenancy.currentLanguage;
  const replacement = replacementConfig?.replacement_mapping[currentLang];

  if (updatedUrl.includes(replacementConfig?.parameter)) {
    updatedUrl = updatedUrl.replace(replacementConfig.parameter, replacement);
  } else {
    return url;
  }

  return updatedUrl;
}

/**
 * Updates booking URL that contains a certain condition
 * @param {*} overriderConfig Configuration object that contains mapping and parameter to updated.
 * @param {*} url Url that potentially contains string to be updated.
 */
export function overrideBookingUrl(overriderConfig, url) {
  let updatedUrl = formatUrlWithoutProtocol(url);

  if (updatedUrl.includes(overriderConfig?.condition)) {
    updatedUrl = updatedUrl + overriderConfig.paramToAdd;
  }
  
  return updatedUrl;
}

/**
 * Adds the URL Protocol if the URL doesn't have it
 * @param {string} website Website URL that needs to modify
 */
export const formatUrlWithoutProtocol = (website) => {
  if (website.startsWith('http://') || website.startsWith('https://')) {
    return website;
  }

  return `https://${website}`;
};

/**
 * Checks the first index of filtered badge if its an Essilor Expert logo
 * @param {string} filteredBadge Badge logo link usually in html img format
 * @param {string} 
 * @returns Boolean
 */
export const checkIsEexpertBadge = (filteredBadge, enableBadgeExpertsToOfferFlag) => {
  const EexpertBadge = '<img src="https://ecplocator-images.s3.amazonaws.com/badges/NewBlueEssilorExperts.png" class="eee-logo">';

  // Return true if EExpert is matched
  if (enableBadgeExpertsToOfferFlag && (filteredBadge === EexpertBadge)) {
    return true;
  }

  return false;
};
